import { useState, useEffect } from 'react';
import { formatCurrency } from '../utils/calculations';

const DEFAULT_ITEMS = [
  { name: 'Kitchen',        base: 20000 },
  { name: 'Bathroom',       base: 10000 },
  { name: 'Flooring',       base: 8000  },
  { name: 'Roofing',        base: 12000 },
  { name: 'Siding',         base: 12000 },
  { name: 'Paint Interior', base: 5000  },
  { name: 'Paint Exterior', base: 7000  },
  { name: 'Windows',        base: 10000 },
  { name: 'HVAC',           base: 10000 },
  { name: 'Electrical',     base: 15000 },
  { name: 'Plumbing',       base: 12000 },
  { name: 'Foundation',     base: 15000 },
  { name: 'Landscaping',    base: 5000  },
  { name: 'Basement',       base: 20000 },
  { name: 'Addition',       base: 60000 },
];

export default function RehabEstimator({ onChange, location, mao }) {
  const [items, setItems] = useState(
    DEFAULT_ITEMS.map(item => ({ ...item, selected: false, cost: item.base, aiLow: null, aiMid: null, aiHigh: null, aiNote: '' }))
  );
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDone, setAiDone] = useState(false);
  const [aiError, setAiError] = useState('');
  const [quality, setQuality] = useState('Mid');

  const selectedItems = items.filter(i => i.selected);
  const totalCost = selectedItems.reduce((sum, i) => sum + i.cost, 0);
  const adjustedOffer = mao != null ? Math.max(0, mao - totalCost) : null;

  useEffect(() => {
    if (onChange) {
      onChange(totalCost, selectedItems.map(i => ({
        name: i.name, low: i.aiLow ?? Math.round(i.cost * 0.7), mid: i.cost, high: i.aiHigh ?? Math.round(i.cost * 1.4), selected: true
      })));
    }
  }, [items]);

  const toggleItem = (idx) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, selected: !item.selected } : item));
    setAiDone(false);
  };

  const updateCost = (idx, value) => {
    const num = parseInt(value.replace(/[^0-9]/g, '')) || 0;
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, cost: num } : item));
  };

  const getAIEstimate = async () => {
    if (selectedItems.length === 0) { setAiError('Select at least one rehab item first.'); return; }
    setAiLoading(true);
    setAiError('');
    setAiDone(false);

    try {
      const payload = {
        sqft: 1500,
        location: location || 'US average market',
        items: selectedItems.map(i => ({
          name: i.name, selected: true,
          low: Math.round(i.base * 0.7), mid: i.base, high: Math.round(i.base * 1.4)
        }))
      };

      const res  = await fetch('/api/ai/rehab-estimate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      const data = await res.json();

      setItems(prev => prev.map(item => {
        const ai = data.items?.find(a => a.name === item.name);
        if (!ai || !item.selected) return item;
        const midCost = quality === 'Low' ? ai.aiLow : quality === 'High' ? ai.aiHigh : ai.aiMid;
        return { ...item, cost: midCost || item.cost, aiLow: ai.aiLow, aiMid: ai.aiMid, aiHigh: ai.aiHigh, aiNote: ai.aiNote || '' };
      }));
      setAiDone(true);
    } catch {
      setAiError('AI estimate failed. Using local estimates.');
    }
    setAiLoading(false);
  };

  const applyQuality = (q) => {
    setQuality(q);
    if (!aiDone) return;
    setItems(prev => prev.map(item => {
      if (!item.selected || !item.aiLow) return item;
      const cost = q === 'Low' ? item.aiLow : q === 'High' ? item.aiHigh : item.aiMid;
      return { ...item, cost: cost || item.cost };
    }));
  };

  const qualityColors = { Low: '#10b981', Mid: 'var(--primary)', High: '#8b5cf6' };

  return (
    <div>
      {/* Quality selector */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
          Select items to include in rehab scope:
        </span>
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg-dark)', padding: 4, borderRadius: 8, border: '1px solid var(--border)' }}>
          {['Low', 'Mid', 'High'].map(q => (
            <button key={q} onClick={() => applyQuality(q)} style={{
              padding: '5px 18px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: '0.82rem', fontFamily: 'inherit', transition: 'all 0.2s',
              background: quality === q ? qualityColors[q] : 'transparent',
              color: quality === q ? 'white' : 'var(--text-muted)'
            }}>{q}</button>
          ))}
        </div>
      </div>

      {/* Item grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginBottom: 16 }}>
        {items.map((item, idx) => (
          <div
            key={item.name}
            onClick={() => toggleItem(idx)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              background: item.selected ? 'rgba(37,99,235,0.07)' : 'var(--bg-dark)',
              borderRadius: 8, border: `1.5px solid ${item.selected ? 'var(--primary)' : 'var(--border)'}`,
              cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none'
            }}
          >
            <div style={{
              width: 16, height: 16, borderRadius: 4, border: `2px solid ${item.selected ? 'var(--primary)' : 'var(--border)'}`,
              background: item.selected ? 'var(--primary)' : 'transparent', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              {item.selected && <span style={{ color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: 1 }}>✓</span>}
            </div>
            <span style={{ fontSize: '0.88rem', fontWeight: 600, color: item.selected ? 'var(--text-primary)' : 'var(--text-secondary)', flex: 1 }}>
              {item.name}
            </span>
            {item.selected && (
              <input
                type="text"
                value={`$${item.cost.toLocaleString()}`}
                onClick={e => e.stopPropagation()}
                onChange={e => updateCost(idx, e.target.value)}
                style={{
                  width: 84, padding: '3px 6px', fontSize: '0.82rem', textAlign: 'right',
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 5, color: 'var(--text-primary)', fontFamily: 'inherit'
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* AI Estimate button */}
      {selectedItems.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {aiError && (
            <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 7, color: '#ef4444', fontSize: '0.82rem', marginBottom: 10 }}>
              {aiError}
            </div>
          )}
          <button
            onClick={getAIEstimate}
            disabled={aiLoading}
            style={{
              width: '100%', padding: '11px 0', borderRadius: 9,
              background: aiDone ? 'rgba(16,185,129,0.08)' : 'var(--primary)',
              border: aiDone ? '1.5px solid rgba(16,185,129,0.35)' : '1.5px solid transparent',
              color: aiDone ? '#10b981' : '#fff', fontWeight: 700, fontSize: '0.9rem',
              cursor: aiLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8, transition: 'all 0.2s', fontFamily: 'inherit'
            }}
          >
            {aiLoading ? (
              <><div className="spinner" style={{ width: 16, height: 16 }} /> Getting AI Estimate for {location || 'your market'}...</>
            ) : aiDone ? (
              <>AI Estimate Applied — Click to Refresh</>
            ) : (
              <>Get AI Rehab Estimate for {location || 'this market'}</>
            )}
          </button>
          {aiDone && (
            <p style={{ textAlign: 'center', fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 6 }}>
              Costs updated based on local labor &amp; material pricing. Toggle Low / Mid / High to adjust.
            </p>
          )}
        </div>
      )}

      {/* AI breakdown table */}
      {aiDone && selectedItems.some(i => i.aiLow) && (
        <div style={{ marginBottom: 16, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{
            padding: '10px 16px', background: 'var(--bg-dark)', borderBottom: '1px solid var(--border)',
            display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px', gap: 8,
            fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.05em'
          }}>
            <span>Item</span>
            <span style={{ textAlign: 'right' }}>Low</span>
            <span style={{ textAlign: 'right', color: 'var(--primary-light)' }}>Mid</span>
            <span style={{ textAlign: 'right' }}>High</span>
          </div>
          {selectedItems.filter(i => i.aiLow).map(item => (
            <div key={item.name} style={{
              padding: '9px 16px', borderBottom: '1px solid var(--border)',
              display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px', gap: 8, alignItems: 'center',
              background: 'var(--bg-card)'
            }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{item.name}</span>
              <span style={{ textAlign: 'right', fontSize: '0.82rem', color: '#10b981', fontWeight: 600 }}>{formatCurrency(item.aiLow)}</span>
              <span style={{ textAlign: 'right', fontSize: '0.85rem', fontWeight: 700, color: 'var(--primary-light)' }}>{formatCurrency(item.aiMid)}</span>
              <span style={{ textAlign: 'right', fontSize: '0.82rem', color: '#f59e0b', fontWeight: 600 }}>{formatCurrency(item.aiHigh)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Total + Adjusted Offer */}
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{
          flex: 1, padding: '16px 20px',
          background: 'rgba(37,99,235,0.07)', border: '1.5px solid var(--primary)', borderRadius: 10,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Total Rehab</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--primary-light)' }}>{formatCurrency(totalCost)}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{selectedItems.length} items · {quality} quality</div>
          </div>
          {aiDone && (
            <div style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 600, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', padding: '4px 10px', borderRadius: 6 }}>
              AI Estimated
            </div>
          )}
        </div>

        {adjustedOffer != null && totalCost > 0 && (
          <div style={{
            flex: 1, padding: '16px 20px',
            background: 'rgba(16,185,129,0.06)', border: '1.5px solid rgba(16,185,129,0.35)', borderRadius: 10,
          }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Adjusted Offer Price</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#10b981' }}>{formatCurrency(adjustedOffer)}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>MAO − Rehab = Offer to Seller</div>
          </div>
        )}
      </div>
    </div>
  );
}
